import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'

type OuvidoriaForm = {
  cpf: string
  ticket: string
  isTicketValid: boolean
  fullName: string
  email: string
  phone: string
  cnpj: string
  companyName: string
  attachments: File[]
  attachmentUrls: string[]
}

const initialFormState: OuvidoriaForm = {
  cpf: '',
  ticket: '',
  isTicketValid: false,
  fullName: '',
  email: '',
  phone: '',
  cnpj: '',
  companyName: '',
  attachments: [],
  attachmentUrls: [],
}

type OuvidoriaState = {
  formState: OuvidoriaForm
  setFormState: Dispatch<SetStateAction<OuvidoriaForm>>
  currentStep: number
  setCurrentStep: Dispatch<SetStateAction<number>>
  cancelModalOpen: boolean
  setCancelModalOpen: Dispatch<SetStateAction<boolean>>
  resetState: () => void
}

const OuvidoriaContext = createContext<OuvidoriaState | null>(null)

export function OuvidoriaProvider({ children }: { children: ReactNode }) {
  const [formState, setFormState] = useState(initialFormState)
  const [currentStep, setCurrentStep] = useState(1)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const resetState = useCallback(() => {
    setFormState(initialFormState)
    setCurrentStep(1)
    setCancelModalOpen(false)
  }, [])

  return (
    <OuvidoriaContext.Provider
      value={{
        formState,
        setFormState,
        currentStep,
        setCurrentStep,
        cancelModalOpen,
        setCancelModalOpen,
        resetState,
      }}
    >
      {children}
    </OuvidoriaContext.Provider>
  )
}

export function useOuvidoriaState() {
  const state = useContext(OuvidoriaContext)
  if (!state) throw new Error('useOuvidoriaState requires OuvidoriaProvider')
  return state
}
